import { 
  collection,
  doc, 
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
// import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'; // Firebase Storage
import { db, auth } from './config'; // Added auth import
import { updateUserReputation, ACTIVITY_TYPES } from './userReputation'; // Import reputation system
import { getPresignedUploadUrl, uploadFileToR2, getR2PublicUrl } from '../services/r2Service'; // R2 Service

// Get notifications for a user (comments on their sites and replies to their comments)
export const getUserNotifications = async (userId) => {
  try {
    const [sitesResponse, userCommentsQuery] = await Promise.all([
      // Get the user's sites
      getDocs(query(collection(db, 'sites'), where('submittedBy', '==', userId))),
      // Get all the user's comments to check for replies
      getDocs(query(collection(db, 'site_discussions'), where('userId', '==', userId)))
    ]);

    const userSiteIds = sitesResponse.docs.map(doc => doc.id);
    const userCommentIds = userCommentsQuery.docs.map(doc => doc.id);

    // Batch site comments queries (30 IDs per query)
    const siteCommentsQueries = [];
    for (let i = 0; i < userSiteIds.length; i += 30) {
      const batchIds = userSiteIds.slice(i, i + 30);
      if (batchIds.length > 0) {
        siteCommentsQueries.push(
          getDocs(
            query(
              collection(db, 'site_discussions'),
              where('siteId', 'in', batchIds),
              where('userId', '!=', userId),
              orderBy('userId'),
              orderBy('createdAt', 'desc')
            )
          )
        );
      }
    }

    // Batch replies queries (30 IDs per query)
    const repliesQueries = [];
    for (let i = 0; i < userCommentIds.length; i += 30) {
      const batchIds = userCommentIds.slice(i, i + 30);
      if (batchIds.length > 0) {
        repliesQueries.push(
          getDocs(
            query(
              collection(db, 'site_discussions'),
              where('replyTo', 'in', batchIds),
              orderBy('createdAt', 'desc')
            )
          )
        );
      }
    }

    // If no queries, return empty results
    if (siteCommentsQueries.length === 0) siteCommentsQueries.push(Promise.resolve({ docs: [] }));
    if (repliesQueries.length === 0) repliesQueries.push(Promise.resolve({ docs: [] }));

    // Execute all batched queries
    const [siteCommentsSnapshots, repliesSnapshots] = await Promise.all([
      Promise.all(siteCommentsQueries),
      Promise.all(repliesQueries)
    ]);

    // Combine results from all batches
    const siteComments = siteCommentsSnapshots.flatMap(snap => 
      snap.docs.map(doc => ({
        id: doc.id,
        type: 'site_comment',
        ...doc.data()
      }))
    );

    const commentReplies = repliesSnapshots.flatMap(snap => 
      snap.docs.map(doc => ({
        id: doc.id,
        type: 'comment_reply',
        ...doc.data()
      }))
    );

    const notifications = {
      siteComments,
      commentReplies
    };

    return { success: true, data: notifications };
  } catch (error) {
    console.error('Error getting notifications:', error);
    return { success: false, error: 'Failed to get notifications' };
  }
};

// Get discussions for a site
export const getSiteDiscussions = async (siteId) => {
  try {
    // Get discussions without server-side sorting
    const discussionsRef = collection(db, 'site_discussions');
    const discussionsQuery = query(
      discussionsRef,
      where('siteId', '==', siteId)
    );
    const discussionsSnap = await getDocs(discussionsQuery);
    return {
      success: true,
      // Sort by likes length client-side
      data: discussionsSnap.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .sort((a, b) => (b.likes?.length || 0) - (a.likes?.length || 0))
    };
  } catch (error) {
    console.error('Error getting site discussions:', error);
    return { success: false, error: 'Failed to get discussions' };
  }
};

// Upload a comment image to R2
export const uploadCommentImage = async (file, siteId, onProgress) => {
  try {
    if (!siteId) {
      throw new Error("Site ID is required to upload comment image.");
    }
    // Define a prefix for comment images within a site's media folder in R2
    // This keeps comment images organized under the site they belong to.
    const r2ObjectKeyPrefix = `sites/${siteId}/comment_images/`;
    const uniqueFileName = `${Date.now()}-${file.name.replace(/\s+/g, '_')}`;
    const objectKey = `${r2ObjectKeyPrefix}${uniqueFileName}`;

    // 1. Get presigned URL from our worker
    // We pass the full intended objectKey to the worker if it needs it,
    // or just the filename if the worker constructs the key.
    // Assuming worker expects filename and contentType and returns the full key.
    // For consistency, let's say our worker takes filename and generates the key with a prefix.
    // OR, we can construct the key here and pass it. Let's assume we pass `objectKey` to `getPresignedUploadUrl`
    // if the worker supports receiving a desired key.
    // For this example, let's stick to the current `getPresignedUploadUrl(filename, contentType)`
    // and the worker will place it in a generic "comment-images" bucket/prefix if not specified.
    // A better approach for `getPresignedUploadUrl` would be to allow specifying a target prefix or full key.
    // Modifying the call to reflect a potential desired key structure:
    // const { uploadUrl, objectKey: returnedKey } = await getPresignedUploadUrl(uniqueFileName, file.type, `sites/${siteId}/comment_images`);
    // For now, using the simpler version and assuming the worker handles placement or we adjust later.
    
    const { uploadUrl, objectKey: actualObjectKey } = await getPresignedUploadUrl(file.name, file.type);

    // 2. Upload the file to R2 using the presigned URL
    await uploadFileToR2(uploadUrl, file, onProgress);
    
    // 3. Get the public URL for the uploaded file
    const publicUrl = getR2PublicUrl(actualObjectKey); // Use the key returned by the presigned URL endpoint

    return {
      success: true,
      url: publicUrl, // This is the public R2 URL
      objectKey: actualObjectKey // This is the R2 object key
    };
  } catch (error) {
    console.error('Error uploading comment image to R2:', error);
    return { success: false, error: `Failed to upload image to R2: ${error.message}` };
  }
};

// Update a comment
export const updateComment = async (siteId, commentId, updates) => {
  try {
    const commentRef = doc(db, 'site_discussions', commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists() || commentDoc.data().siteId !== siteId) {
      return { success: false, error: 'Comment not found' };
    }

    await updateDoc(commentRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });

    return { success: true };
  } catch (error) {
    console.error('Error updating comment:', error);
    return { success: false, error: 'Failed to update comment' };
  }
};

// Add a comment to a site
export const addSiteComment = async (siteId, {
  text,
  userId,
  userName,
  authorName,
  photoUrl,
  replyTo,
  originalParentId,
  type
}) => {
  try {
    // Input validation
    if (!text || typeof text !== 'string' || text.trim().length === 0) {
      return { success: false, error: 'Comment text is required' };
    }

    if (text.length > 2000) {
      return { success: false, error: 'Comment text too long' };
    }

    if (!userId || !userName) {
      return { success: false, error: 'User information required' };
    }

    const siteRef = doc(db, 'sites', siteId);
    const siteDoc = await getDoc(siteRef);

    if (!siteDoc.exists()) {
      return { success: false, error: 'Site not found' };
    }

    // Sanitize text input
    const sanitizedText = text.trim().replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

    const commentData = {
      siteId,
      text: sanitizedText,
      userId,
      userName: userName.substring(0, 100), // Limit username length
      authorName: authorName ? authorName.substring(0, 100) : userName.substring(0, 100),
      photoUrl: photoUrl || '',
      type: type || (replyTo ? 'reply' : 'comment'),
      replyTo: replyTo || null,
      originalParentId: originalParentId || replyTo || null,
      createdAt: serverTimestamp(),
      likes: [],
      flagged: false,
      moderationStatus: 'pending'
    };

    const docRef = await addDoc(collection(db, 'site_discussions'), commentData);
    return { 
      success: true, 
      id: docRef.id 
    };
  } catch (error) {
    console.error('Error adding comment:', error);
    return { success: false, error: 'Failed to add comment' };
  }
};

// Delete a comment
export const deleteComment = async (siteId, commentId) => {
  try {
    const commentRef = doc(db, 'site_discussions', commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists() || commentDoc.data().siteId !== siteId) {
      return { success: false, error: 'Comment not found' };
    }

    await deleteDoc(commentRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting comment:', error);
    return { success: false, error: 'Failed to delete comment' };
  }
};

// Add or remove a reaction from a comment
export const addReaction = async (siteId, commentId, userId, action) => {
  try {
    const commentRef = doc(db, 'site_discussions', commentId);
    const commentDoc = await getDoc(commentRef);

    if (!commentDoc.exists() || commentDoc.data().siteId !== siteId) {
      return { success: false, error: 'Comment not found' };
    }

    const likes = commentDoc.data().likes || [];
    const commentAuthorId = commentDoc.data().userId; // Get the author of the comment
    let updatedLikes;
    let reputationUpdated = false;

    if (action === 'like' && !likes.includes(userId)) {
      updatedLikes = [...likes, userId];
      // Award reputation to the comment author, not the user liking the comment
      if (commentAuthorId && commentAuthorId !== userId) { // Ensure author exists and is not self-liking
        await updateUserReputation(commentAuthorId, ACTIVITY_TYPES.COMMENT_UPVOTE);
        reputationUpdated = true;
      }
    } else if (action === 'unlike' && likes.includes(userId)) {
      updatedLikes = likes.filter(id => id !== userId);
      // Note: Reputation is not typically removed for unliking,
      // but this could be added if required.
    } else {
      return { success: true }; // No changes needed
    }

    await updateDoc(commentRef, { likes: updatedLikes });
    return { success: true, reputationUpdated };
  } catch (error) {
    console.error('Error updating reaction:', error);
    return { success: false, error: 'Failed to update reaction' };
  }
};
